import { useEffect, useState } from "react";
import { api, unwrap } from "@/lib/api";
import { Users, Briefcase, MapPin, Megaphone } from "lucide-react";

/**
 * Client Insights — top occupations · top locations · top referral sources.
 * Powers data-driven marketing decisions from the data captured at intake.
 */
export function ClientInsights({ branchId }) {
  const [data, setData] = useState({
    top_occupations: [], top_locations: [], top_referrals: [],
    total_clients: 0, clients_with_profile: 0,
  });

  useEffect(() => {
    const params = branchId ? { branch_id: branchId } : {};
    api.get("/clients/insights", { params })
      .then((r) => setData(unwrap(r) || {}))
      .catch(() => {});
  }, [branchId]);

  const fill = data.total_clients > 0
    ? Math.round((data.clients_with_profile / data.total_clients) * 100)
    : 0;

  return (
    <div className="glass-primary rounded-[20px] p-6" data-testid="owner-client-insights">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Users size={16} className="text-[var(--color-mauve-deep)]" />
          <h2 className="font-sora font-semibold text-[16px]">Client insights</h2>
        </div>
        <div className="font-inter text-[11.5px] text-[var(--color-text-muted)]">
          Profile coverage:&nbsp;
          <span className="font-mono text-[var(--color-text-primary)] font-semibold">{fill}%</span>
          &nbsp;· {data.clients_with_profile}/{data.total_clients}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <InsightCol
          icon={<Briefcase size={13} />}
          label="Top occupations"
          accent="#5E4060"
          items={data.top_occupations || []}
          empty="Add occupation at intake"
          testid="owner-insights-occupations"
        />
        <InsightCol
          icon={<MapPin size={13} />}
          label="Top locations"
          accent="#8E6A1A"
          items={data.top_locations || []}
          empty="Add location at intake"
          testid="owner-insights-locations"
        />
        <InsightCol
          icon={<Megaphone size={13} />}
          label="Top referral sources"
          accent="#7C3AED"
          items={data.top_referrals || []}
          empty="Add referral at intake"
          testid="owner-insights-referrals"
        />
      </div>
    </div>
  );
}

function InsightCol({ icon, label, accent, items, empty, testid }) {
  const max = items.length > 0 ? items[0].count : 1;
  return (
    <div className="rounded-2xl p-4" style={{ background: `${accent}0D`, border: `1px solid ${accent}30` }} data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-2.5" style={{ color: accent }}>
        {icon}
        <span className="overline" style={{ color: accent }}>{label}</span>
      </div>
      {items.length === 0 ? (
        <div className="font-inter text-[12px] text-[var(--color-text-muted)] italic">{empty}</div>
      ) : (
        <div className="space-y-1.5">
          {items.map((it, i) => {
            const pct = Math.round((it.count / max) * 100);
            return (
              <div key={it.name}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-sora font-medium text-[12.5px] text-[var(--color-text-primary)] truncate">
                    {i === 0 && <span className="mr-1" style={{ color: accent }}>★</span>}
                    {it.name}
                  </span>
                  <span className="font-mono text-[11px]" style={{ color: accent }}>{it.count}</span>
                </div>
                <div className="h-1 rounded-full mt-0.5 overflow-hidden" style={{ background: `${accent}14` }}>
                  <div className="h-full" style={{ width: `${pct}%`, background: accent }} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ClientInsights;
